<template>
	<div>
		<upload-header
			:title="$t('upload.video-manager.title')"
			:subtitle="$t('upload.video-manager.subtitle')"
		/>

		<div class="upload__content">
			<helio-message
				:active="hasError"
				:title="$t('upload.video-manager.error.title')"
				class="upload__video-message"
				icon-token="exclamation-triangle"
				icon-weight="bold"
				state="alert"
				@close="hasError = false"
			>
				<template v-slot:content>
					{{ $t('upload.video-manager.error.description') }}
				</template>
			</helio-message>

			<helio-field
				:display-success="false"
				:initial-value="currentLink"
				:placeholder="$t('upload.video-manager.placeholder')"
				:button="$t('upload.video-manager.upload')"
				:show-errors="false"
				:show-validators="false"
				button-color="primary"
				icon="link"
				icon-weight="light"
				type="url"
				class="upload__video-field"
				@click="submit"
				@enter="submit"
				@changed="currentLink = $event"
			/>

			<upload-box type="horizontal">
				<upload-heading :title="galleryTitle" />

				<upload-grid
					v-if="medias.length > 0"
					ref="grid"
					@scroll.native="debouncedScroll"
				>
					<upload-thumbnail
						v-if="loading"
						:initial-loading="true"
						:api="api"
						type="video"
					/>

					<upload-thumbnail
						v-for="(media, index) in visibleMedias"
						:key="index"
						:media="media"
						:selected="selection.includes(media.id)"
						:can-be-selected="!quantity || selection.length < quantity"
						:api="api"
						extra="author"
						type="video"
						@toggle="$emit('toggle', media.id)"
						@preview="$emit('preview', media)"
						@delete="$emit('delete', $event)"
					/>
				</upload-grid>

				<helio-empty
					v-if="medias.length === 0"
					:initial-title="$t('upload.video-manager.gallery.empty.title')"
					:initial-subtitle="$t('upload.video-manager.gallery.empty.subtitle')"
					illustration="feather"
				/>
			</upload-box>

			<upload-box
				v-if="selection.length > 1"
				type="horizontal"
			>
				<upload-heading :title="$t('upload.orderable.title')" />

				<upload-orderable
					:selection="selection"
					:medias="medias"
					:quantity="quantity"
					@order-changed="$emit('order-changed', $event)"
				/>
			</upload-box>
		</div>

		<upload-footer>
			<helio-button
				:text="$t('upload.video-manager.cancel')"
				@click.stop="$emit('close')"
			/>
			<helio-button
				:disabled="quantity && selection.length < quantity"
				:text="$t('upload.video-manager.save')"
				color="primary"
				@click.stop="$emit('save')"
			/>
		</upload-footer>
	</div>
</template>

<script>
import _debounce from 'lodash/debounce';
import _orderBy from 'lodash/orderBy';
import Button from '../../../button/Button.vue';
import Message from '../../../message/Message.vue';
import Field from '../../../field/Field.vue';
import Empty from '../../../empty/Empty.vue';
import Footer from '../../footer/Footer.vue';
import Header from '../../header/Header.vue';
import Heading from '../../heading/Heading.vue';
import Thumbnail from '../../thumbnail/Thumbnail.vue';
import Orderable from '../../orderable/Orderable.vue';
import Box from '../../box/Box.vue';
import Grid from '../../grid/Grid.vue';
import Centios from '../../../../utils/communication/Centios';

export default {
	components: {
		'helio-button': Button,
		'helio-message': Message,
		'helio-field': Field,
		'helio-empty': Empty,
		'upload-footer': Footer,
		'upload-header': Header,
		'upload-orderable': Orderable,
		'upload-box': Box,
		'upload-thumbnail': Thumbnail,
		'upload-heading': Heading,
		'upload-grid': Grid,
	},
	props: {
		selection: {
			type: Array,
			required: true,
		},
		quantity: {
			type: Number,
			default: 1,
			required: false,
		},
		medias: {
			type: Array,
			required: true,
		},
		api: {
			type: String,
			required: true,
		},
	},
	data() {
		return {
			preview: null,
			loading: false,
			hasError: false,
			limit: 15,
			currentLink: '',
		};
	},
	computed: {
		visibleMedias() {
			return this.orderedMedias.slice(0, this.limit);
		},

		orderedMedias() {
			return _orderBy(this.medias, ['selected'], ['desc']);
		},

		quantityLabel() {
			return this.quantity === 1 ? 'single' : 'multiple';
		},

		galleryTitle() {
			const text = this.$t(`upload.video-manager.gallery.title.${this.quantityLabel}`);

			if (this.quantity > 1) {
				const countdown = `(${this.selection.length}/${this.quantity})`;
				return `${text} ${countdown}`;
			}

			return text;
		},
	},
	beforeCreate() {
		this.$root.$off('add-media-in-gallery');
	},
	created() {
		this.$root.$on('add-media-in-gallery', () => {
			this.loading = false;
		});
	},
	mounted() {
		this.updateGrid();
	},
	methods: {
		submit(url) {
			this.currentLink = '';
			this.hasError = false;
			this.loading = true;

			// This is a voluntary fake loader
			setTimeout(() => {
				if (
					url.includes('youtu.be')
					|| url.includes('youtube.com')
					|| url.includes('vimeo.com')
				) {
					const endpoint = `${this.api}/medias`;
					const formData = new FormData();
					formData.append('upload-method', 'url');
					formData.append('url', url);
					Centios.submit('post', endpoint, formData).then((response) => {
						if (Centios.isValid(response.data)) {
							this.$emit('create', response.data.media);
						} else {
							this.hasError = true;
							this.loading = false;
						}
					});
				} else {
					this.hasError = true;
					this.loading = false;
				}
			}, 350);
		},
		/* eslint-disable-next-line */
		debouncedScroll: _debounce(function (newValue) {
			this.updateGrid();
		}, 15),
		updateGrid() {
			if (this.$refs.grid) {
				const grid = this.$refs.grid.$el;

				if (grid.scrollTop > (grid.scrollHeight - (grid.offsetHeight + 200))) {
					this.limit += 15;
				}
			}
		},
	},
};
</script>
